import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import { isDemoMode } from "../demo/demoMode";
import { DEMO_MERCHANT_ID } from "../demo/demoData";

export type ShopRef = {
  id: string;
  name?: string | null;
  shop_name?: string | null;
  client_name?: string | null;
  shop_domain?: string | null;
};

interface ShopContextType {
  shops: ShopRef[];
  currentShopId: string | null;
  setCurrentShopId: (id: string | null) => void;
  refreshShops: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType>({} as ShopContextType);

export const useShop = () => {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
};

export const ShopProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [shops, setShops] = useState<ShopRef[]>([]);
  const [currentShopId, setCurrentShopIdState] = useState<string | null>(null);

  // Seed selection from localStorage immediately on mount (do not write back)
  useEffect(() => {
    try {
      const saved = localStorage.getItem("flowstock.selectedShopId");
      if (saved) {
        setCurrentShopIdState(saved);
      }
    } catch {}
  }, []);

  const setCurrentShopId = (id: string | null) => {
    setCurrentShopIdState(id);
    try {
      if (id) localStorage.setItem("flowstock.selectedShopId", id);
      else localStorage.removeItem("flowstock.selectedShopId");
    } catch {}
  };

  const refreshShops = async () => {
    if (!user) {
      setShops([]);
      return;
    }

    // Demo mode: provide mock shop without querying
    if (isDemoMode()) {
      const demoShops: ShopRef[] = [{
        id: DEMO_MERCHANT_ID,
        name: 'Urban Style Berlin',
        shop_name: 'Urban Style Berlin',
        client_name: 'Urban Style Berlin',
        shop_domain: 'urbanstyle-berlin.myshopify.com',
      }];
      setShops(demoShops);
      if (!currentShopId) setCurrentShopId(DEMO_MERCHANT_ID);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("auth_users_shops")
        .select("shop_id, merchants(id, name, shop_name, shop_domain, merchant_config!user_config_user_id_fkey(client_name))")
        .eq("auth_user_id", user.id)
        .order("last_access_at", { ascending: false });

      if (error) {
        console.error("Error fetching shops:", error);
        // Don't clear existing shops on error
        return;
      }

      const list = (data || []).map((r: any) => ({
        id: r.shop_id,
        name: r.merchants?.name,
        shop_name: r.merchants?.shop_name,
        client_name: r.merchants?.merchant_config?.client_name,
        shop_domain: r.merchants?.shop_domain
      }));
      console.log("Fetched shops:", list);
      setShops(list);

      // Initialize selection if not set
      if (!currentShopId) {
        // try localStorage first
        let saved: string | null = null;
        try { saved = localStorage.getItem("flowstock.selectedShopId"); } catch {}
        const initial = saved && list.some(s => s.id === saved) ? saved : (list[0]?.id ?? null);
        setCurrentShopId(initial);
      } else if (list.length && !list.some(s => s.id === currentShopId)) {
        // currently selected shop is no longer available
        setCurrentShopId(list[0].id);
      }
    } catch (err) {
      console.error("Exception in refreshShops:", err);
    }
  };

  useEffect(() => {
    refreshShops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo(() => ({ shops, currentShopId, setCurrentShopId, refreshShops }), [shops, currentShopId]);

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};
